import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";

// --- IMPORT BACKEND FUNCTIONS ---
import {
  requestPasswordReset,
  verifyOTP,
  resetPassword,
  resendOTP, // Assuming you exported the resend helper as well
} from "../Backend/authFunction.js";

// --- VALIDATION SCHEMAS ---
const emailSchema = yup.object().shape({
  email: yup.string().email("Invalid email format").required("Email is required"),
});

const otpSchema = yup.object().shape({
  otp: yup.string().matches(/^[0-9]+$/, "Digits only").length(6, "Must be 6 digits").required("OTP is required"),
});

const passwordSchema = yup.object().shape({
  newPassword: yup.string().min(8, "Min 8 characters").matches(/[A-Z]/, "Must contain uppercase").matches(/[0-9]/, "Must contain number").required("New password is required"),
  confirmPassword: yup.string().oneOf([yup.ref("newPassword"), null], "Passwords must match").required("Confirm Password is required"),
});

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [resendTimer, setResendTimer] = useState(120); // 2 Minute Timer

  // Timer logic for Step 2
  useEffect(() => {
    let interval;
    if (step === 2 && resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer, step]);

  const currentSchema = step === 1 ? emailSchema : step === 2 ? otpSchema : passwordSchema;

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: step < 4 ? yupResolver(currentSchema) : undefined,
    mode: "onChange",
  });

  // --- HANDLERS ---

  // STEP 1: Send OTP
  const handleStep1_SendOTP = async (data) => {
    setLoading(true);
    try {
      const result = await requestPasswordReset(data.email);
      if (result.success) {
        setEmail(data.email);
        setStep(2);
        setResendTimer(120); // Initialize timer
        Swal.fire({ icon: "success", title: "OTP Sent", text: `Check your inbox at ${data.email}`, timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: "error", title: "Error", text: result.message, confirmButtonColor: "#E63946" });
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong.", "error");
    } finally { setLoading(false); }
  };

  // STEP 2: Verify OTP
  const handleStep2_VerifyOTP = async (data) => {
    setLoading(true);
    try {
      // Logic adjusted to match: verifyOTP(email, otp)
      const result = await verifyOTP(email, data.otp);
      if (result.success) {
        setStep(3);
      } else {
        Swal.fire({ icon: "error", title: "Invalid Code", text: result.message, confirmButtonColor: "#E63946" });
      }
    } catch (error) {
      Swal.fire("Error", "Verification failed.", "error");
    } finally { setLoading(false); }
  };

  // STEP 3: Reset Password
  const handleStep3_ResetPassword = async (data) => {
    setLoading(true);
    try {
      // Logic adjusted to match: resetPassword(email, newPassword)
      const result = await resetPassword(email, data.newPassword);
      if (result.success) {
        setStep(4);
      } else {
        Swal.fire({ icon: "error", title: "Update Failed", text: result.message, confirmButtonColor: "#E63946" });
      }
    } catch (error) {
      Swal.fire("Error", "Failed to reset password", "error");
    } finally { setLoading(false); }
  };

  // Resend OTP logic
  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    const result = await resendOTP(email);
    setLoading(false);
    if (result.success) {
      setResendTimer(120);
      Swal.fire({ icon: "success", title: "Resent", text: "A new code has been sent.", timer: 2000, showConfirmButton: false });
    }
  };

  const onSubmit = (data) => {
    if (step === 1) handleStep1_SendOTP(data);
    if (step === 2) handleStep2_VerifyOTP(data);
    if (step === 3) handleStep3_ResetPassword(data);
  };

  const renderProgressBar = () => (
    <div className="flex items-center gap-2 mb-8">
      {[1, 2, 3].map((num) => (
        <div key={num} className="flex-1 h-1.5 rounded-full transition-all duration-300" 
             style={{ backgroundColor: step >= num ? "#E63946" : "#E2E8F0" }}></div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F6F8FB] font-sans">
      {/* LEFT SIDE - BRANDING (Kept exactly as per your design) */}
      <div className="hidden lg:flex w-5/12 bg-[#0B1C2D] relative overflow-hidden items-center justify-center p-12">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#1a2f46] via-[#0B1C2D] to-[#0B1C2D]"></div>
        <div className="absolute bottom-1/4 -right-12 w-96 h-96 bg-[#E63946]/10 rounded-full blur-[100px]"></div>
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-3 group cursor-pointer mb-6">
            <div className="flex items-center justify-center size-10 rounded-lg bg-[#E63946] text-white shadow-lg shadow-[#E63946]/20">
              <span className="material-symbols-outlined">hub</span>
            </div>
            <span className="text-xl font-bold tracking-tight text-white">ContactHQ</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">Secure Account <br /><span className="text-[#E63946]">Recovery.</span></h1>
          <p className="text-gray-400 text-lg leading-relaxed mb-10">We'll verify your identity and get you back into your dashboard in no time.</p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="material-symbols-outlined text-[#E63946]">lock</span>
            <span>256-bit Encrypted Process</span>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - FORM */}
      <div className="flex-1 flex items-center justify-center p-6">
        {step === 4 ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md bg-white rounded-2xl shadow-xl p-10 text-center">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-green-600 text-4xl">check_circle</span>
            </div>
            <h2 className="text-2xl font-bold text-[#0B1C2D] mb-2">Password Reset!</h2>
            <p className="text-gray-500 mb-8">Your password has been updated. You can now log in with your new credentials.</p>
            <button onClick={() => navigate("/login")} className="w-full bg-[#0B1C2D] hover:bg-[#152a40] text-white font-bold py-3.5 rounded-lg shadow-lg transition-all">Go to Login</button>
          </motion.div>
        ) : (
          <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-3xl shadow-xl border border-gray-100 relative">
            <div className="text-center mb-8">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E63946]/10 text-[#E63946] mb-4">
                <span className="material-symbols-outlined text-2xl">{step === 1 ? "lock_reset" : step === 2 ? "password" : "key"}</span>
              </div>
              <h2 className="text-2xl font-bold text-[#0B1C2D]">{step === 1 ? "Forgot Password?" : step === 2 ? "Verify OTP" : "Reset Password"}</h2>
              <p className="mt-2 text-sm text-gray-500">
                {step === 1 && "Enter your email to receive a reset code."}
                {step === 2 && `Enter the 6-digit code sent to ${email}`}
                {step === 3 && "Create a strong new password."}
              </p>
            </div>

            {renderProgressBar()}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div key="step1" variants={slideUp} initial="hidden" animate="visible" exit="exit">
                    <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">Email Address</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 material-symbols-outlined text-[20px]">email</span>
                      <input {...register("email")} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none" placeholder="name@company.com" />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="step2" variants={slideUp} initial="hidden" animate="visible" exit="exit">
                    <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">One-Time Password (OTP)</label>
                    <input {...register("otp")} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none text-center text-xl tracking-widest font-mono" placeholder="000000" maxLength={6} />
                    {errors.otp && <p className="text-red-500 text-xs mt-1">{errors.otp.message}</p>}
                    <div className="mt-4 text-center">
                      <p className="text-xs text-gray-400 mb-1">Didn't get the code?</p>
                      <button type="button" onClick={handleResend} disabled={resendTimer > 0} className={`text-sm font-semibold ${resendTimer > 0 ? "text-gray-300" : "text-[#E63946] hover:underline"}`}>
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="step3" variants={slideUp} initial="hidden" animate="visible" exit="exit" className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">New Password</label>
                      <input type="password" {...register("newPassword")} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none" placeholder="Min 8 chars" />
                      {errors.newPassword && <p className="text-red-500 text-xs mt-1">{errors.newPassword.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">Confirm Password</label>
                      <input type="password" {...register("confirmPassword")} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none" placeholder="Retype password" />
                      {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button type="submit" disabled={loading} className="w-full bg-[#E63946] hover:bg-[#d12f3c] text-white font-bold py-3.5 rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all">
                {loading ? <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> : (step === 1 ? "Send Reset Code" : step === 2 ? "Verify & Continue" : "Update Password")}
              </button>

              {step === 1 && (
                <div className="text-center">
                  <Link to="/login" className="text-sm font-semibold text-gray-500 hover:text-[#0B1C2D] flex items-center justify-center gap-1"><span className="material-symbols-outlined text-sm">arrow_back</span>Back to Login</Link>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;